import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, MessageSquare, FileText, AlertTriangle, Clock, Trash2, CheckCircle, Trash } from 'lucide-react';
import notificationService, { Notification } from '../../services/notificationService';
import { useHighlightContext } from '../../contexts/HighlightContext';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-hot-toast';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  STALE_TIME_MS,
  defaultRefetchPolicy,
  queryKeys,
  invalidateNotificationsTree,
} from '../../config/reactQuery';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const orgId = organization?.id ?? null;
  const { highlightItem } = useHighlightContext();
  const { isConnected } = useSocket();
  const [page, setPage] = useState(1);

  const dropdownListFilters = useMemo(() => ({ limit: 10, filter: 'all' as const }), []);

  // Fetch notifications
  const { data: notificationData, isLoading } = useQuery({
    queryKey: queryKeys.notificationsList(orgId, page, dropdownListFilters),
    queryFn: () => notificationService.getNotifications({ page, limit: 10 }),
    refetchInterval: isOpen && !isConnected ? 60_000 : false,
    staleTime: STALE_TIME_MS.notificationsList,
    enabled: isOpen,
    ...defaultRefetchPolicy,
  });

  // Note: Sound is now played when notification arrives via socket (in SocketContext)
  // We don't play sound when opening the dropdown to view existing notifications

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => notificationService.markAsRead(notificationId),
    onSuccess: () => {
      invalidateNotificationsTree(queryClient);
      // Dispatch event to update unread count in header
      window.dispatchEvent(new CustomEvent('notification-read'));
    }
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      invalidateNotificationsTree(queryClient);
      // Dispatch event to update unread count in header
      window.dispatchEvent(new CustomEvent('notification-read'));
    }
  });

  // Delete notification mutation
  const deleteNotificationMutation = useMutation({
    mutationFn: (notificationId: string) => notificationService.deleteNotification(notificationId),
    onSuccess: () => {
      invalidateNotificationsTree(queryClient);
      // Dispatch event to update unread count in header
      window.dispatchEvent(new CustomEvent('notification-read'));
    }
  });

  // Clear all notifications mutation
  const clearAllNotificationsMutation = useMutation({
    mutationFn: () => notificationService.clearAllNotifications(),
    onSuccess: () => {
      invalidateNotificationsTree(queryClient);
      // Dispatch event to update unread count in header
      window.dispatchEvent(new CustomEvent('notification-read'));
      toast.success('All notifications cleared');
    },
    onError: (error: any) => {
      // Clear notifications error
      const errorMessage = error?.response?.data?.message || 'Failed to clear notifications';
      toast.error(errorMessage);
    }
  });

  // Handle notification click
  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read
    await markAsReadMutation.mutateAsync(notification.id);
    
    // Get navigation data with entity context
    const navData = notificationService.getNotificationNavigationData(notification);
    
    // Navigate to the relevant page with entity context
    if (navData.entityId && navData.entityType) {
      // Store entity context for the target page to use
      sessionStorage.setItem('notificationContext', JSON.stringify({
        entityId: navData.entityId,
        entityType: navData.entityType,
        timestamp: Date.now()
      }));

      // Also highlight the item
      highlightItem(navData.entityId, navData.entityType, notification.id);
    }
    
    navigate(navData.path);
    onClose();
  };

  // Handle delete notification
  const handleDeleteNotification = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation(); // Prevent triggering the click handler
    await deleteNotificationMutation.mutateAsync(notificationId);
  };

  // Get notification icon
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'approval_required':
      case 'approval_reminder':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'approval_overdue':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'approval_processed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'mention':
        return <MessageSquare className="w-5 h-5 text-blue-500" />;
      case 'task_assignment':
        return <CheckCircle className="w-5 h-5 text-purple-500" />;
      case 'gst_reminder':
        return <FileText className="w-5 h-5 text-orange-500" />;
      case 'system_alert':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  // Get notification priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'normal':
        return 'bg-blue-100 text-blue-800';
      case 'low':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 bg-blue-50 px-6 py-4">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-white rounded-lg shadow-sm">
            <Bell className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
            {notificationData?.data?.unread_count !== undefined && (
              <p className="text-xs text-gray-500">
                {notificationData.data.unread_count > 0 
                  ? `${notificationData.data.unread_count} unread` 
                  : 'All caught up'}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-1">
          {notificationData?.data?.unread_count && notificationData.data.unread_count > 0 && (
            <button
              onClick={() => markAllAsReadMutation.mutate()}
              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-all "
              disabled={markAllAsReadMutation.isPending}
              title="Mark all as read"
            >
              <CheckCheck className="w-5 h-5" />
            </button>
          )}
          {notificationData?.data?.notifications && notificationData.data.notifications.length > 0 && (
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to clear all notifications? This action cannot be undone.')) {
                  clearAllNotificationsMutation.mutate();
                }
              }}
              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-all "
              disabled={clearAllNotificationsMutation.isPending}
              title="Clear all notifications"
            >
              <Trash className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      <div className="max-h-[32rem] overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div>
            <p className="text-sm text-gray-500 mt-3">Loading notifications...</p>
          </div>
        ) : notificationData?.data?.notifications?.length === 0 ? (
          <div className="text-center py-16 px-6">
            <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
              <Bell className="w-8 h-8 text-blue-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">All caught up!</h4>
            <p className="text-sm text-gray-500">You don&apos;t have any notifications right now.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notificationData?.data?.notifications?.map((notification: any) => (
              <div
                key={notification.id}
                className={`p-4 cursor-pointer transition-all hover:bg-gray-50 relative group ${
                  !notification.is_read ? 'bg-blue-50/50' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                {!notification.is_read && (
                  <div className="absolute bottom-0 left-0 top-0 w-1 bg-blue-600"></div>
                )}
                
                <div className="flex items-start space-x-3 pl-2">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className={`p-2 rounded-lg ${
                      notification.is_read ? 'bg-gray-100' : 'bg-white shadow-sm'
                    }`}>
                      {getNotificationIcon(notification.type)}
                    </div>
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-semibold leading-snug ${
                        notification.is_read ? 'text-gray-700' : 'text-gray-900'
                      }`}>
                        {notification.title}
                      </p>
                      <div className="flex items-center space-x-1 flex-shrink-0">
                        {notification.priority !== 'normal' && (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getPriorityColor(notification.priority)}`}>
                            {notification.priority}
                          </span>
                        )}
                        <button
                          onClick={(e) => handleDeleteNotification(e, notification.id)}
                          className="opacity-0 group-hover:opacity-100 p-1.5 hover:bg-red-100 rounded-lg transition-all"
                          title="Delete notification"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </div>
                    
                    <p className={`text-sm mt-1 leading-relaxed ${
                      notification.is_read ? 'text-gray-500' : 'text-gray-700'
                    }`}>
                      {notificationService.formatNotificationMessage(notification)}
                    </p>
                    
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center text-xs text-gray-400">
                        <Clock className="w-3.5 h-3.5 mr-1" />
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </div>
                      {notification.is_read && (
                        <div className="flex items-center text-xs text-green-600">
                          <CheckCheck className="w-3.5 h-3.5 mr-1" />
                          Read
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {notificationData?.data?.pagination?.totalPages && notificationData.data.pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-white rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-700 disabled:hover:bg-transparent"
          >
            ← Previous
          </button>
          
          <span className="text-sm font-medium text-gray-600">
            {page} / {notificationData?.data?.pagination?.totalPages || 1}
          </span>
          
          <button
            onClick={() => setPage(Math.min(notificationData?.data?.pagination?.totalPages || 1, page + 1))}
            disabled={page === (notificationData?.data?.pagination?.totalPages || 1)}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-blue-600 hover:bg-white rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-700 disabled:hover:bg-transparent"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

